import styled from 'styled-components';
import React, { Component } from 'react';
import { push, replace } from 'redux-first-history';
import { AccessLevel, CompressionFormat, DeliveryLogType, ExportFileFormats, MessageCategory } from '@dev/ee-api/ee-api-models';
import { Container, Paper, PrimaryButton, TabsPanel } from '@dev/ee-components';
import { flatten, isEmpty, isNil, omitBy } from 'lodash-es';
import { parse, stringify } from 'query-string';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { generatePath, withRouter } from 'react-router';
import { compose } from 'redux';

import dayjs from '@api/services/tools/dayjs';
import { ExportLogsParams, ResendParams } from '@api/services/ApiCategories/logs';
import { debounce } from '@api/services/tools/debounce';
import { addParamToUrl, parseUrlParams, setAtEndOfDay } from '@api/services/tools/handleUrlParams';
import { ApplicationState } from '@api/store';
import { GetCampaigns } from '@api/store/campaigns/actions';
import { GetCampaignsPayload } from '@api/store/campaigns/types';
import { LoadChannelsStart } from '@api/store/channels/actions';
import { ChannelsPayload } from '@api/store/channels/types';
import { ShowDialog } from '@api/store/dialog/actions';
import { ShowDialogParams } from '@api/store/dialog/types';
import { ViewEmailStart } from '@api/store/emails/actions';
import { ExportLogs, LoadLogsStart, LoadNotificationLogs, ResendStart, ClearLogList } from '@api/store/logs/actions';
import { LogTypesString, StdLoadLogsInterface } from '@api/store/logs/types';
import { DEFAULT_PER_PAGE_COUNT, LIMITED_LOGS_DAYS, LIMITED_LOGS_DAYS_PRO } from '@api/services/constants';

import ExportModal from '../ExportModal';
import { convertMessageCategoryAndStatuses, splitSelectedItemsIntoProperState } from './helperFunctions';
import { LogsInterface, LogTypesNumber } from './Interface';
import { LogsTable } from './assets/LogsTable';
import { LogsFiltersBar } from './assets/LogsFiltersBar';
import { ExtendedLogsBanner } from './assets/ExtendedLogsBanner';
import { DashboardPaths, StatisticsPaths } from '@api/services/dictionaries/paths';
import { INITIAL_PER_PAGE } from '@api/store/exports/types';

const DATE_FORMAT = 'YYYY-MM-DD';

const MobileWrapper = styled.div`
    & div:last-child {
        & > div {
            & .MuiPagination-root {
                margin-top: 8px;
            }
        }
    }
`;

const mapState = (state: ApplicationState, ownProps: LogsInterface): LogsInterface => {
    return {
        ...ownProps,
        browser: state.browser,
        logs: state.logs,
        channels: state.channels,
        statistics: state.statistics,
        account: state.account,
        campaigns: state.campaigns,
        config: state.config,
    };
};

const dispatchProps = {
    ClearLogList,
    LoadLogsStart,
    LoadNotificationLogs,
    LoadChannelsStart,
    ViewEmailStart,
    ShowDialog,
    Push: push,
    Replace: replace,
    ExportLogs,
    ResendStart,
    GetCampaigns,
};

type DispatchProps = {
    ClearLogList(): void;
    LoadLogsStart(params: StdLoadLogsInterface): void;
    LoadNotificationLogs(params: StdLoadLogsInterface): void;
    LoadChannelsStart(params?: ChannelsPayload): void;
    GetCampaigns(params: GetCampaignsPayload): void;
    ViewEmailStart(msgid: string): void;
    ShowDialog(params: ShowDialogParams): void;
    Push(url: string | Record<string, any>): void;
    Replace(param: Record<string, string> | string): void;
    ExportLogs(params: ExportLogsParams): void;
    ResendStart: (params: ResendParams) => void;
};

const logTypes = ['email', 'notification', 'sms'];

type Props = DispatchProps & LogsInterface;

class Logs extends Component<Props> {
    public state = {
        selectedChannel: null,
        selectedResult: null,
        statuses: null,
        to: dayjs().utc().toJSON(),
        from: dayjs().subtract(this.getLimitedLogDays(), 'day').startOf('day').utc().toJSON(),
        perPage: DEFAULT_PER_PAGE_COUNT,
        exportOptions: {
            channelNames: [],
            fileName: null,
            compressionFormat: CompressionFormat.Zip,
            fileFormat: ExportFileFormats.Csv,
        },
        emailSearch: '',
        dateFilterValue: this.getLimitedLogDays(),
        activePage: 1,
        refresh: false,
        singleContactToShowInOverlay: '',
        selectedCampaign: null,
    };

    private getLimitedLogDays(): number {
        const { GeneralInfo } = this.props?.account || {};
        return GeneralInfo?.KeepLogsForDays;
    }

    public componentDidMount(): void {
        if (this.loadLogsFromUrl()) {
            return;
        }

        if (!this.isLogsScreen(LogTypesString.NOTIFICATION)) {
            return this.loadEmailsLogsData();
        }
        return this.loadNotificationLogsData();
    }

    public componentWillUnmount(): void {
        this.props.ClearLogList();
    }

    /**
     * * seting state based on query string
     * * params available on query string:
     * @param status
     * @param channel
     * @param result
     * @param page
     */
    private loadLogsFromUrl(): boolean {
        const { location } = this.props;
        const query = parseUrlParams(location.search);
        if (isEmpty(query)) {
            return false;
        }
        const urlStatuses: string[] = query.statuses ? flatten([query.statuses]) : null;
        const urlResult: string[] = query.result ? flatten([query.result]) : null;
        let dateParams = {};
        if (query?.from && query?.to) {
            const from = dayjs(query?.from as string)
                .startOf('day')
                .utc();
            const to = dayjs(query?.to as string);
            const dateDiff = to.diff(from, 'day');

            const dateOptions = this.getDateDropdownOptions();
            const selectedOption = dateOptions?.find((el) => +el.value === +dateDiff)?.value || 'calendar';
            dateParams = {
                from: from.toJSON(),
                to: to.toJSON(),
                dateFilterValue: selectedOption,
            };
        }
        const valuesBasedOnQuery = {
            selectedChannel: query?.channel,
            selectedCampaign: query?.campaign,
            statuses: urlStatuses,
            selectedResult: urlResult,
            emailSearch: query?.search,
            perPage: DEFAULT_PER_PAGE_COUNT,
            activePage: 1,
            ...dateParams,
        };

        this.setState({ ...valuesBasedOnQuery }, () => {
            this.updateLogs();
            if (!this.isLogsScreen(LogTypesString.NOTIFICATION)) {
                this.loadChannelsAndSummary();
            }
        });
        return true;
    }

    private changeExportCompression(enabled: boolean): void {
        const { exportOptions } = this.state;

        const updatedValues = {
            ...exportOptions,
            compressionFormat: enabled ? CompressionFormat.Zip : CompressionFormat.None,
        };
        this.setState({ exportOptions: updatedValues });
    }
    private changeExportFileFormat(format: ExportFileFormats): void {
        const { exportOptions } = this.state;
        this.setState({ exportOptions: { ...exportOptions, fileFormat: format } });
    }
    private inProgressFileNameRef = React.createRef<HTMLInputElement>();

    private exportInProgress(): void {
        const { ExportLogs } = this.props;
        const { exportOptions, from, to, selectedChannel, selectedCampaign, selectedResult, emailSearch, statuses } = this.state;
        const [convertedStatuses, convertedResults] = convertMessageCategoryAndStatuses(statuses, selectedResult);
        const fileName = this.inProgressFileNameRef.current.value || 'export';
        const fileFormat: ExportFileFormats = exportOptions.fileFormat;

        const exportParams = {
            fileName,
            statuses: isEmpty(convertedStatuses) ? [DeliveryLogType.All] : (convertedStatuses as DeliveryLogType[]),
            channelName: selectedChannel || selectedCampaign,
            messageCategory: isEmpty(selectedResult) ? null : convertedResults,
            from,
            to: setAtEndOfDay(to),
            fileFormat,
            email: emailSearch,
            compressionFormat: exportOptions.compressionFormat,
            isNotification: this.isLogsScreen(LogTypesString.NOTIFICATION),
        };

        const result: ExportLogsParams = omitBy(exportParams, isNil) as unknown as ExportLogsParams;

        ExportLogs(result);
    }

    private openExportModal(): void {
        const { ShowDialog, t } = this.props;

        ShowDialog({
            title: t('export'),
            body: (
                <ExportModal
                    onCompressionChange={this.changeExportCompression.bind(this)}
                    onFileFormatChange={this.changeExportFileFormat.bind(this)}
                    inputRef={this.inProgressFileNameRef}
                />
            ),
            confirmLabel: t('export'),
            cancelLabel: t('cancel'),
            onClickConfirm: this.exportInProgress.bind(this),
        });
    }

    private loadNotificationLogsData(): void {
        const { to, from, statuses, perPage, activePage, emailSearch } = this.state;
        const { LoadNotificationLogs } = this.props;

        const params: StdLoadLogsInterface = {
            from,
            to: setAtEndOfDay(to),
            statuses: statuses ?? [DeliveryLogType.All],
            inProgressLogPage: activePage || 1,
            logLoading: true,
            perPage,
            email: emailSearch,
        };
        LoadNotificationLogs(params);
    }

    private loadChannelsAndSummary(): void {
        const { to, from } = this.state;
        const { LoadChannelsStart, GetCampaigns, account } = this.props;
        const filtersParams = {
            from,
            to: setAtEndOfDay(to),
        };

        const { AccountAccessLevel } = account?.GeneralInfo || {};

        if (AccountAccessLevel?.includes(AccessLevel.ViewChannels)) {
            LoadChannelsStart({ limit: INITIAL_PER_PAGE, offset: 0, skipSagaURL: true, ...filtersParams });
        }
        if (AccountAccessLevel?.includes(AccessLevel.ViewCampaigns)) {
            GetCampaigns({ limit: 0, offset: 0, ...filtersParams });
        }
    }

    private loadEmailsLogsData(): void {
        const { LoadLogsStart } = this.props;
        const { to, from, statuses, perPage, selectedChannel, selectedResult, selectedCampaign } = this.state;

        this.loadChannelsAndSummary();

        const params: StdLoadLogsInterface = {
            statuses: isEmpty(statuses) ? [DeliveryLogType.All] : (statuses as DeliveryLogType[]),
            inProgressLogPage: 1,
            channelName: selectedChannel ?? selectedCampaign,
            messageCategory: isEmpty(selectedResult) ? null : selectedResult,
            skipSagaURL: true,
            includeSms: this.isLogsScreen(LogTypesString.SMS),
            includeEmail: this.isLogsScreen(LogTypesString.EMAIL),
            onlyLastEvent: this.isLogsScreen(LogTypesString.SMS),
        };
        LoadLogsStart({ from, to: setAtEndOfDay(to), perPage, ...params });
    }

    public componentDidUpdate(prevProps): void {
        const {
            match: {
                params: { logType: prevType },
            },
        } = prevProps;
        const {
            match: {
                params: { logType },
            },
        } = this.props;
        const isTabChanged = prevType !== logType;
        if (isTabChanged) {
            this.setState(
                {
                    selectedChannel: null,
                    selectedResult: null,
                    selectedCampaign: null,
                    statuses: null,
                    activePage: 1,
                },
                () => {
                    return this.isLogsScreen(LogTypesString.NOTIFICATION) ? this.loadNotificationLogsData() : this.loadEmailsLogsData();
                }
            );
        }
    }

    private changeTabHandler(event: React.ChangeEvent<HTMLElement>, value: any): void {
        const {
            match: { path },
            location,
            Replace,
            ClearLogList,
        } = this.props;

        const newPath = generatePath(path, { logType: logTypes[value] });
        const parsedSearch = parse(location.search);
        const savedFilters = stringify({
            from: parsedSearch?.from,
            to: parsedSearch?.to,
        });
        ClearLogList();
        this.setState({ emailSearch: '' });
        Replace(`${newPath}?${savedFilters}`);
    }

    private getHeaderOptions() {
        const {
            match: {
                params: { logType },
            },

            logs: { logList },
            account: {
                GeneralInfo: { AccountAccessLevel },
            },
            t,
            config,
            Push,
        } = this.props;

        const isMobile = this.isMobile();
        const canSeeSMSLogs = AccountAccessLevel.includes(AccessLevel.SendSMS);
        const canExportLogs = AccountAccessLevel.includes(AccessLevel.Export);

        const isLoadedInMarketingProduct = config?.routerBasePath === DashboardPaths.Marketing;

        const tabs = [
            {
                label: t('emaillogs'),
            },
            {
                label: t('notificationlog'),
            },
            ...(canSeeSMSLogs
                ? [
                      {
                          label: t('smslog'),
                      },
                  ]
                : []),
        ];

        const value = LogTypesNumber[logType];

        return {
            title: t('logs'),
            isMobile,
            tabs,
            value,
            onChange: this.changeTabHandler.bind(this),
            additionalHeaderContent: canExportLogs && (
                <PrimaryButton text={t('export')} onClick={this.openExportModal.bind(this)} disabled={isEmpty(logList?.Recipients)} />
            ),
            disableSentenceCase: true,
            ...(isLoadedInMarketingProduct && {
                breadcrumbs: t('activity'),
                breadcrumbsOnClick: () => Push(StatisticsPaths.StatisticsOverview),
            }),
        };
    }

    private isLogsScreen(checkedScreenType: LogTypesString | LogTypesString[]): boolean {
        const {
            match: {
                params: { logType },
            },
        } = this.props;

        const availableScreens = {
            [LogTypesString.EMAIL]: logType === LogTypesString.EMAIL,
            [LogTypesString.NOTIFICATION]: logType === LogTypesString.NOTIFICATION,
            [LogTypesString.SMS]: logType === LogTypesString.SMS,
        };

        return flatten([checkedScreenType])?.some((screen) => availableScreens?.[screen]);
    }

    private getDateDropdownOptions = () => {
        const { GeneralInfo } = this.props?.account;
        const hasExtendedLogs = this.canSeeExtendedLogs();
        const logsDays = GeneralInfo.KeepLogsForDays === LIMITED_LOGS_DAYS ? LIMITED_LOGS_DAYS : LIMITED_LOGS_DAYS_PRO;
        const options = [
            {
                key: 1,
                borderBottom: false,
                label: this.props.i18n.t('last24hours') as string,
                value: 1,
            },
            {
                key: logsDays,
                borderBottom: false,
                label: this.props.i18n.t('lastdays', { days: logsDays }) as string,
                value: logsDays,
            },
        ];

        if (hasExtendedLogs) {
            options.push(
                {
                    key: 14,
                    borderBottom: false,
                    label: this.props.i18n.t('lastdays', { days: 14 }) as string,
                    value: 14,
                },
                {
                    key: 35,
                    borderBottom: false,
                    label: this.props.i18n.t('lastdays', { days: 35 }) as string,
                    value: 35,
                }
            );
        }
        const specificTime = [
            {
                borderBottom: true,
                label: this.props.i18n.t('specificdaterange') as string,
                value: 'calendar' as string,
            },
        ];

        return this.isMobile() ? [...options, ...specificTime] : [...specificTime, ...options];
    };

    private setDatesForLogs(event: any, selectedItem): void {
        const { value } = selectedItem;
        const to = dayjs();
        const from = dayjs().subtract(+value, 'day').startOf('day');

        this.addSearchQuery({ from: from.format(DATE_FORMAT), to: to.format(DATE_FORMAT) });
        this.setState({ from: from.utc().toJSON(), to: to.utc().toJSON(), dateFilterValue: +value, activePage: 1 }, () => {
            this.updateLogs();
        });
    }

    private setDatesBasedOnCalendar(fromTime: Date, toTime: Date): void {
        const from = dayjs(fromTime).startOf('day');
        const to = dayjs(toTime);

        this.addSearchQuery({ from: from.format(DATE_FORMAT), to: to.format(DATE_FORMAT) });
        this.setState({ from: from.utc().toJSON(), to: to.utc().toJSON(), activePage: 1 }, () => {
            this.updateLogs();
        });
    }

    private updateLogs(logLoading = true): void {
        const { LoadLogsStart, LoadNotificationLogs } = this.props;
        const { to, from, selectedChannel, selectedResult, selectedCampaign, perPage, emailSearch, activePage, statuses } = this.state;
        const [convertedStatuses, convertedResults] = convertMessageCategoryAndStatuses(statuses, selectedResult);
        const params: StdLoadLogsInterface = {
            from,
            to: setAtEndOfDay(to),
            statuses: isEmpty(convertedStatuses) ? [DeliveryLogType.All] : (convertedStatuses as DeliveryLogType[]),
            channelName: selectedChannel || selectedCampaign,
            messageCategory: isEmpty(selectedResult) ? null : convertedResults,
            inProgressLogPage: activePage || 1,
            perPage,
            email: emailSearch,
            skipSagaURL: true,
            includeSms: this.isLogsScreen(LogTypesString.SMS),
            includeEmail: this.isLogsScreen(LogTypesString.EMAIL),
            logLoading,
        };

        if (this.isLogsScreen(LogTypesString.NOTIFICATION)) {
            return LoadNotificationLogs({
                from,
                email: emailSearch,
                to: setAtEndOfDay(to),
                inProgressLogPage: activePage || 1,
                perPage,
                statuses: isEmpty(convertedStatuses) ? [DeliveryLogType.All] : (convertedStatuses as DeliveryLogType[]),
                messageCategory: isEmpty(selectedResult) ? null : convertedResults,
                logLoading,
            });
        }
        LoadLogsStart(params);
    }

    /**
     *
     * * Function searches logs, condition is that email have to start with provided input
     * @param event input change event
     */

    private searchLogsWhereEmailStartWith(emailSearch: string): void {
        this.setState({ emailSearch, activePage: 1 }, () => {
            if (emailSearch) {
                debounce(() => {
                    this.updateLogs();
                }, 1000);
            } else {
                this.setState({ emailSearch: '', activePage: 1 }, () => this.updateLogs());
            }
        });
    }

    private handleRefresh(): void {
        this.updateLogs();
    }

    private renderOptionBar(): any {
        const { emailSearch, dateFilterValue } = this.state;

        return (
            <LogsFiltersBar
                emailSearch={emailSearch}
                dateFilterValue={dateFilterValue}
                onClicks={{
                    refresh: this.handleRefresh.bind(this),
                    campaignFilter: this.onCampaignFilterClick.bind(this),
                    channelFilter: this.onChannelFilterClick.bind(this),
                    dateFilter: this.setDatesForLogs.bind(this),
                    calendar: this.setDatesBasedOnCalendar.bind(this),
                    search: this.searchLogsWhereEmailStartWith.bind(this),
                }}
                onMobileFilterChange={this.loadLogsFromUrl.bind(this)}
                availableFilters={{
                    channel: true,
                    campaign: true,
                }}
            />
        );
    }

    private onChannelFilterClick(_event: React.SyntheticEvent<HTMLInputElement>, channel: { value: string; label: string }): void {
        const isAll = channel.value === 'all';
        const selectedChannel = isAll ? null : channel.value;

        this.setState({ selectedChannel, selectedCampaign: null, activePage: 1 }, (): void => this.updateLogs());
    }
    private onCampaignFilterClick(_event: React.SyntheticEvent<HTMLInputElement>, channel: { value: string; label: string }): void {
        const isAll = channel.value === 'all';
        const selectedCampaign = isAll ? null : channel.value;

        this.setState({ selectedChannel: null, selectedCampaign, activePage: 1 }, (): void => this.updateLogs());
    }

    private onStatusFilterClick(
        _event: React.SyntheticEvent<HTMLInputElement>,
        selectedItems: (DeliveryLogType | MessageCategory)[]
    ): void {
        const [statuses, selectedResult] = splitSelectedItemsIntoProperState(selectedItems);

        debounce(() => {
            this.addSearchQuery({ statuses, result: selectedResult });
            this.setState({ statuses, selectedResult, activePage: 1 }, (): void => {
                this.updateLogs();
            });
        }, 1500);
    }

    private addSearchQuery(toAdd: Record<string, any>): void {
        const { Replace } = this.props;

        const newPath = addParamToUrl(toAdd);

        Replace({ search: newPath });
    }

    private canSeeExtendedLogs = () => {
        const { GeneralInfo } = this.props.account;
        return GeneralInfo?.AccountAccessLevel?.includes(AccessLevel.ExtendedLogs);
    };

    private fetchNextOnScroll(): void {
        this.setState(
            (prevProps: any) => ({ activePage: prevProps.activePage + 1 }),
            () => this.updateLogs(false)
        );
    }

    private isMobile(): boolean {
        const {
            browser: {
                lessThan: { hd },
            },
        } = this.props;

        return hd;
    }

    render(): JSX.Element {
        const { statuses, selectedResult } = this.state;
        const headerOptions = this.getHeaderOptions();
        const isMobile = this.isMobile();
        const selectedCheckboxes = [...(statuses ?? []), ...(selectedResult ?? [])];

        const ComponentWrapper = isMobile ? MobileWrapper : Paper;
        return (
            <Container variant="full">
                <TabsPanel {...headerOptions} />
                <ExtendedLogsBanner />
                <ComponentWrapper margin={{ top: 32 }}>
                    {this.renderOptionBar()}
                    <LogsTable
                        onHeadersEventFilterClick={this.onStatusFilterClick.bind(this)}
                        selectedEventsCheckboxes={selectedCheckboxes}
                        fetchNextOnScroll={this.fetchNextOnScroll.bind(this)}
                    />
                </ComponentWrapper>
            </Container>
        );
    }
}

export default compose(withRouter, withTranslation(), connect<LogsInterface, DispatchProps>(mapState, dispatchProps))(Logs);
